import React, { createContext, useEffect, useState } from "react";
import {
  ADMIN_AFFILIATE_COMISSION_URL,
  ADMIN_REVENEU_URL,
  AFFILIATE_SUBSCRIPTION,
  AGENCY_USER_ACTIVITY,
  BOT_COMPLIANCE,
  CHATBOT_CONTACT_LIST,
  CHATBOT_CONTACT_LIST_EXPORT_CSV,
  CHATBOT_VISITOR_CONTACT_LIST_EXPORT_CSV,
  CMS_BASE_URL,
  CONTACT_US,
  DOWNLOAD_AGENCY_USER_ACTIVITY,
  LOGIN,
  ME,
  ROLE_ADD_UPDATE,
  ROLE_LIST,
  ROUTER_MODEL_CRUD,
  SIGN_UP,
} from "../utils/APIEndPoints";
import { CallAPI } from "../utils/apiActions";
import { useNavigate } from "react-router-dom";
import { showToast } from "../helper/toastHelper";
import { LABEL } from "../utils/constant";
import { date } from "yup";
import ExportCSV from "../utils/ExportCSV";
import { useCallAPI } from "../hooks/useCallAPI";

/**
 * Initial state for the Admin context.
 * @type {Object}
 * @property {Array} usersList - List of users.
 * @property {Array} couponList - List of coupons.
 * @property {Array} planList - List of plans.
 * @property {Array} planDescriptionList - List of plan descriptions.
 * @property {Array} cmsList - List of CMS content.
 * @property {Array} contactUsUsers - List of contact us submissions.
 * @property {Array} reveneuList - List of revenue records.
 * @property {Object} totalRevenue - Aggregated revenue data.
 * @property {boolean} isLoading - Global loading state.
 * @property {boolean} isCSVLoading - Loading state for CSV exports.
 * @property {Array} roleList - List of roles.
 * @property {Array} userActivityOfAgency - List of agency user activities.
 * @property {Array} affiliateCommissionList - List of affiliate commissions.
 * @property {Array} baseModelList - List of base models.
 * @property {Array} chatbotContactList - List of chatbot contacts.
 * @property {Array} chatbotOwnMetaData - List of chatbot metadata.
 */
const coolBizAdminState = {
  usersList: [],
  couponList: [],
  planList: [],
  planDescriptionList: [],
  cmsList: [],
  contactUsUsers: [],
  reveneuList: [],
  totalRevenue: {},
  isLoading: false,
  isCSVLoading: false,
  isVisitorCSVLoading: false,
  roleList: [],
  userActivityOfAgency: [],
  affiliateCommissionList: [],
  baseModelList: [],
  chatbotContactList: [],
  chatbotOwnMetaData: [],
};

const AdminContext = createContext({
  ...coolBizAdminState,
});

/**
 * Provider component for the Admin Context.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The AdminContext Provider.
 */
export const AdminProvider = ({ children }) => {
  const navigate = useNavigate();
  const [state, dispatch] = useState(coolBizAdminState);

  const {
    data: roleRes,
    loading: roleLoading,
    revalidate: getRole,
  } = useCallAPI({
    end_point: "USER",
    method: "GET",
    apiUrl: "",
    enabled: false,
  });

  const {
    data: allUserRes,
    loading: allUserLoading,
    revalidate: fetchAllUser,
  } = useCallAPI({
    end_point: "USER",
    method: "GET",
    apiUrl: "",
    enabled: false,
  });

  const {
    data: chatbotAllMetaRes,
    loading: chatbotAllMetaLoading,
    revalidate: fetchChatBotAllMeta,
  } = useCallAPI({
    end_point: "USER",
    method: "GET",
    apiUrl: "",
    enabled: false,
  });

  const {
    data: chatbotOwnMetaRes,
    loading: chatbotOwnMetaLoading,
    revalidate: fetchChatBotOwnMeta,
  } = useCallAPI({
    end_point: "USER",
    method: "GET",
    apiUrl: "",
    enabled: false,
  });

  /**
   * Deletes a user by ID.
   * @param {string} userID - The ID of the user to delete.
   * @param {Function} cb - Callback function on success.
   */
  const deleteUsers = async (userID = "", cb) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "DELETE",
      `${CMS_BASE_URL.DELETE_USERS_BY_ADMIN}/${parseInt(userID)}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      cb(response.res.data.message);
    }
  };

  /**
   * Creates a new user.
   * @param {number} [userType=2] - The type of user to create.
   * @param {Object} data - User data.
   * @param {Function} cb - Callback function on success.
   */
  const createUsers = async (userType = 2, data, cb) => {
    const la_data = {
      ...data,
      userType: parseInt(userType),
      role: data.role ? parseInt(data?.role) : parseInt(userType),
    };
    try {
      manageAdminState("isLoading", true);
      const response = await CallAPI(
        "USER",
        "POST",
        `${CMS_BASE_URL.CREATE_USERS_BY_ADMIN}`,
        la_data,
      );
      manageAdminState("isLoading", false);
      if (response?.res?.status === 201) {
        cb(response.res.data.message);
      }
    } catch (error) {
      console.log(error, "error");
    }
  };

  /**
   * Edits an existing user.
   * @param {string} id - The ID of the user to edit.
   * @param {Object} data - Updated user data.
   * @param {Function} cb - Callback function on success.
   */
  const EditUsers = async (id, data, cb) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "PUT",
      `${CMS_BASE_URL.EDIT_USERS_BY_ADMIN}/${id}`,
      data,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      cb(response.res.data.message);
    }
  };

  /**
   * Exports user data as CSV.
   * @param {number} [userType=2] - User type filter.
   * @param {Object} filterData - Filter criteria (search, filter).
   * @param {Array} [userIds=[]] - Specific user IDs to export.
   * @param {Function} cb - Callback function (unused in implementation but present in signature).
   */
  const exportCSV = async (userType = 2, filterData, userIds = [], cb) => {
    manageAdminState("isCSVLoading", true);

    const params = new URLSearchParams({
      userType,
    });

    userIds.forEach((id) => {
      params.append("userIds[]", id);
    });

    // Add any extra keys dynamically
    Object.entries(filterData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value);
      }
    });

    const response = await CallAPI(
      "USER",
      "GET",
      `${CMS_BASE_URL.DOWNLOAD_USERS_BY_ADMIN}?${params.toString()}`,
    );
    manageAdminState("isCSVLoading", false);
    console.log("resPPPPPPP", response);
    if (response?.status && response?.res?.data) {
      const fileName =
        response?.res?.headers?.["content-disposition"] ||
        response?.res?.headers?.["Content-Disposition"] ||
        "";
      ExportCSV(response.res.data, fileName);
    } else {
      showToast(response?.msg || "Failed to export CSV", LABEL.TOAST_TYPE.ERROR);
    }
  };

  /**
   * Creates a new coupon.
   * @param {Object} body - Coupon data.
   * @param {Function} cb - Callback function on success.
   */
  const createCoupon = async (body, cb) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "POST",
      CMS_BASE_URL.COUPON.CREATE,
      body,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      cb(response.res.data.message);
    }
  };

  /**
   * Fetches all coupons.
   * @param {Object} payload - Payload containing user ID.
   * @param {number} [page=1] - Page number.
   */
  const getAllCoupons = async (payload, page = 1) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "GET",
      `${CMS_BASE_URL.COUPON.GET_ALL}?page=${page}&userId=${payload.userId}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      manageAdminState("couponList", response?.res?.data?.data || {});
    }
  };

  /**
   * Deletes a coupon.
   * @param {Object} payload - Payload containing coupon ID.
   * @param {Function} cb - Callback function on success.
   */
  const deleteCoupon = async (payload, cb) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "POST",
      CMS_BASE_URL.COUPON.DELETE,
      payload,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      cb(response.res.data.message);
    }
  };

  /**
   * Fetches all plans.
   * @param {Object} payload - Payload containing user ID.
   * @param {number} [page=1] - Page number.
   * @param {string} [currency="India"] - Currency context.
   */
  const getAllPlan = async (payload, page = 1, currency = "India") => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "GET",
      `${CMS_BASE_URL.PLAN.GET_ALL}?page=${page}&userId=${payload.userId}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      manageAdminState("planList", response?.res?.data?.data || []);
    }
  };

  /**
   * Creates or updates a plan.
   * @param {Object} body - Plan data.
   * @param {Function} cb - Callback function on success.
   * @param {string} [id] - Plan ID if updating.
   */
  const createPlan = async (body, cb, id) => {
    console.log("body", body.currency);
    manageAdminState("isLoading", true);
    const apidetails = {
      url: id ? CMS_BASE_URL.PLAN.UPDATE : CMS_BASE_URL.PLAN.CREATE,
      body: id ? { ...body, planId: id } : body,
    };
    let subscriptionService =
      body.currency == "inr" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
    const response = await CallAPI(
      subscriptionService,
      "POST",
      apidetails.url,
      apidetails.body,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.data?.status) {
      cb(response.res.data.message);
    }
  };

  /**
   * Updates plan price/details.
   * @param {Object} body - Plan data.
   * @param {Function} cb - Callback function on success.
   */
  const editPlan = async (body, cb) => {
    manageAdminState("isLoading", true);
    let subscriptionService =
      body.currency == "inr" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
    const response = await CallAPI(
      subscriptionService,
      "POST",
      CMS_BASE_URL.PLAN.UPDATE_PRICE,
      body,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.data?.status === 200) {
      cb(response.res.data.message);
    }
  };

  /**
   * Fetches plan descriptions.
   * @param {Object} payload - Payload for fetching descriptions.
   */
  const getAllPlanDescription = async (payload) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "POST",
      CMS_BASE_URL.PLAN_DESCRIPTION.GET_ALL,
      payload,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      manageAdminState("planDescriptionList", response?.res?.data?.data || []);
    }
  };

  /**
   * Fetches a plan by ID.
   * @param {string} id - Plan ID.
   * @returns {Promise<Object>} The plan data.
   */
  const getPlanById = async (id) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "GET",
      `${CMS_BASE_URL.PLAN.GET_BY_ID}${id}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      return response?.res?.data?.data || [];
    }
  };

  /**
   * Creates CMS content.
   * @param {Object} body - CMS data.
   * @param {Function} cb - Callback function on success.
   * @param {string} type - CMS type.
   */
  const createCms = async (body, cb, type) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "POST",
      CMS_BASE_URL.CMS.CREATE,
      body,
      type,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.data?.status === 201) {
      cb(response.res.data.message);
    }
  };

  /**
   * Updates CMS content.
   * @param {Object} body - CMS data.
   * @param {Function} cb - Callback function on success.
   * @param {string} id - CMS ID.
   * @param {string} type - CMS type.
   */
  const updateCms = async (body, cb, id, type) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "PUT",
      `${CMS_BASE_URL.CMS.UPDATE}${id}`,
      body,
      type,
    );
    manageAdminState("isLoading", false);
    console.log(response);
    if (response?.res?.data?.success) {
      cb(response.res.data.message);
    }
  };

  /**
   * Fetches all CMS content.
   * @param {Object} data - Filter data.
   * @param {number} [type=1] - CMS type.
   * @param {number} [page=1] - Page number.
   * @param {number} [limit=10] - Items per page.
   */
  const getAllCMS = async (data, type = 1, page = 1, limit = 10) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "POST",
      `${CMS_BASE_URL.CMS.GET_ALL}${type}?page=${page}`,
      data,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      manageAdminState("cmsList", response?.res?.data?.data || []);
    }
  };

  /**
   * Fetches contact us submissions.
   * @param {number} [page=1] - Page number.
   */
  const getAllContactUs = async (page = 1) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI("USER", "GET", `${CONTACT_US}?page=${page}`);
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      manageAdminState("contactUsUsers", response?.res?.data?.data || []);
    }
  };

  /**
   * Fetches CMS content by ID.
   * @param {string} id - CMS ID.
   * @returns {Promise<Object>} The CMS data.
   */
  const getCMSById = async (id) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "GET",
      `${CMS_BASE_URL.CMS.GET_BY_ID}${id}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      // manageAdminState('cmsList', response?.res?.data?.data || []);
      return response?.res?.data?.data || [];
    }
  };

  /**
   * Deletes CMS content.
   * @param {string} id - CMS ID.
   * @param {Function} cb - Callback function on success.
   */
  const deleteCms = async (id, cb) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "DELETE",
      `${CMS_BASE_URL.CMS.DELETE}${id}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.status === 200) {
      cb(response.res.data.message);
    }
  };
  /**
   * Fetches revenue list.
   * @param {number} [page=1] - Page number.
   * @param {string} [name=""] - Name filter.
   * @param {number} accessType - User access type.
   * @param {string} userId - User ID.
   */
  const getReveneuList = async (page = 1, name = "", accessType, userId) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "GET",
      `${ADMIN_REVENEU_URL}?page=${page}&fullName=${name}&parentId=${
        accessType === 3 ? userId : ""
      }`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.data?.status === 200) {
      console.log("res_user", response?.res?.data?.data);
      manageAdminState("reveneuList", response?.res?.data?.data || []);
      manageAdminState(
        "totalRevenue",
        totalRevenueModifiedObj(response?.res?.data?.data?.revenue_total) || [],
      );
    } else manageAdminState("reveneuList", []);
  };

  /**
   * Formats revenue data into an object keyed by currency.
   * @param {Array} data - Revenue data array.
   * @returns {Object} Formatted revenue object.
   */
  const totalRevenueModifiedObj = (data) => {
    return data.reduce((acc, item) => {
      acc[item.currency] = {
        total: item.total,
        tax: item.tax,
        currency: item.currency,
      };
      return acc;
    }, {});
  };

  /**
   * Exports revenue report as CSV.
   * @param {string} [parentId=""] - Parent ID filter.
   */
  const handleExportReveneuCSV = async (parentId = "") => {
    manageAdminState("isCSVLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION_OTHERS",
      "GET",
      `${CMS_BASE_URL.DOWNLOAD_REVENEU_REPORT}?parentId=${parentId}`,
    );
    manageAdminState("isCSVLoading", false);
    console.log("resPPPPPPP", response);
    if (response.res.status) {
      // const fileName = response?.res?.headers?.get('Content-Disposition')||""
      ExportCSV(response.res.data, "");
    }
  };

  /**
   * Fetches role list.
   * @param {number} [page=1] - Page number.
   * @param {string} [name=""] - Search term.
   */
  const getRoleList = async (page = 1, name = "") => {
    getRole(undefined, `${ROLE_LIST}?page=${page}&search=${name}`);
  };

  /**
   * Updates a specific key in the admin state.
   * @param {string} key - The state key to update.
   * @param {*} value - The new value for the key.
   */
  const manageAdminState = (key, value) =>
    dispatch((prev) => ({ ...prev, [key]: value }));

  /**
   * Fetches all users.
   * @param {number} [userType=2] - User type filter.
   * @param {number} [page=1] - Page number.
   * @param {Object} filterData - Filter criteria.
   */

  const getAllUsers = async (userType = 2, page = 1, filterData = {}) => {
    const params = new URLSearchParams({
      page,
      userType,
    });

    // Add any extra keys dynamically
    Object.entries(filterData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString().trim());
      }
    });

    fetchAllUser(undefined, `${CMS_BASE_URL.ALL_USERS}?${params.toString()}`);
  };

  // const getAllUsers = async (userType = 2, page = 1, filterData) => {
  //   // console.log(filterData, "filterData");

  //   if (filterData.subscriptionStartDate && filterData.subscriptionEndDate) {
  //     fetchAllUser(
  //       undefined,
  //       `${CMS_BASE_URL.ALL_USERS}?page=${page}&search=${filterData.search}&filter=${filterData.filter}&userType=${userType}&subscriptionStartDate=${filterData.subscriptionStartDate}&subscriptionEndDate=${filterData.subscriptionEndDate}`,
  //     );
  //   } else {
  //     fetchAllUser(
  //       undefined,
  //       `${CMS_BASE_URL.ALL_USERS}?page=${page}&search=${filterData.search}&filter=${filterData.filter}&userType=${userType}`,
  //     );
  //   }
  // };

  /**
   * Creates or updates a role.
   * @param {Object} body - Role data.
   * @param {Function} cb - Callback function on success.
   */
  const createUpdateRole = async (body, cb) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI("USER", "POST", ROLE_ADD_UPDATE, body);
    manageAdminState("isLoading", false);
    if (response?.res?.data?.status === 200) {
      cb(response.res.data.message);
    }
  };
  /**
   * Fetches agency user activity.
   * @param {number} [page=1] - Page number.
   * @param {string} [search=""] - Search term.
   */
  const getUserActivityOfAgency = async (page = 1, search = "") => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "GET",
      `${AGENCY_USER_ACTIVITY}?page=${page}&limit=10&search=${search}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.data?.status === 200) {
      console.log("res_user", response?.res?.data?.data);
      manageAdminState("userActivityOfAgency", response?.res?.data?.data || []);
    } else manageAdminState("userActivityOfAgency", []);
  };
  /**
   * Downloads agency user activity as CSV.
   * @param {string} [search=""] - Search term.
   */
  const downloadUserActivityOfAgency = async (search = "") => {
    manageAdminState("isCSVLoading", true);
    const response = await CallAPI(
      "USER",
      "GET",
      `${DOWNLOAD_AGENCY_USER_ACTIVITY}?search=${search}&parentAgencyIds=[]`,
    );
    manageAdminState("isCSVLoading", false);
    console.log("resPPPPPPP", response);
    if (response.res.status) {
      const fileName = response.res.headers.get("Content-Disposition");
      ExportCSV(response.res.data, fileName);
    }
  };
  /**
   * Fetches affiliate commission list.
   * @param {number} [page=1] - Page number.
   * @param {Object} date - Date range object {startDate, endDate}.
   */
  const getaffiliateCommisionList = async (page = 1, date) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "GET",
      `${ADMIN_AFFILIATE_COMISSION_URL}?page=${page}&startDate=${
        date.startDate || ""
      }&endDate=${date.endDate || ""}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.data?.status === 200) {
      console.log("res_user", response?.res?.data?.data);
      manageAdminState(
        "affiliateCommissionList",
        response?.res?.data?.data || [],
      );
    } else manageAdminState("affiliateCommissionList", []);
  };

  /**
   * Exports affiliate commission data as CSV.
   * @param {Object} date - Date range object {startDate, endDate}.
   */
  const handleExportCommissionCSV = async (date) => {
    manageAdminState("isCSVLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION",
      "GET",
      `${AFFILIATE_SUBSCRIPTION.EXPORT_CSV}?startDate=${
        date?.startDate || ""
      }&endDate=${date?.endDate || ""}`,
    );
    manageAdminState("isCSVLoading", false);

    if (response.res.status) {
      // const fileName = response?.res?.headers?.get('Content-Disposition')||""
      ExportCSV(response.res.data, "", false, true);
    }
  };
  /**
   * Fetches admin added base models.
   * @param {number} [page=1] - Page number.
   * @param {string} [name=""] - Search term.
   */
  const getAdminAddedBaseModel = async (page = 1, name = "") => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "GET",
      `${ROUTER_MODEL_CRUD.LIST}?page=${page}&limit=10&search=${name}`,
    );
    manageAdminState("isLoading", false);
    console.log("res_model", response?.res);
    if (response?.res?.data?.status) {
      console.log("res_model2", response?.res?.data?.dataObj);
      manageAdminState("baseModelList", response?.res?.data?.dataObj || []);
    } else manageAdminState("baseModelList", []);
  };
  /**
   * Deletes a CMS file.
   * @param {string} fileId - File ID.
   */
  const deleteCMSFile = async (fileId) => {
    const response = await CallAPI(
      "USER",
      "DELETE",
      `${CMS_BASE_URL.CMS.DELETE_FILE}/${fileId}`,
    );
    // if (response?.res?.status === 200) {
    //   cb(response.res.data.message);
    // }
  };
  /**
   * Fetches chatbot contact list.
   * @param {number} [page=1] - Page number.
   * @param {Object} date - Date range (unused in implementation).
   */
  const getChatbotContactList = async (page = 1, date) => {
    manageAdminState("isLoading", true);
    const response = await CallAPI(
      "CHAT",
      "GET",
      `${CHATBOT_CONTACT_LIST}?page=${page}`,
    );
    manageAdminState("isLoading", false);
    if (response?.res?.data?.status === 200) {
      console.log("res_contact", response?.res?.data?.data);
      manageAdminState("chatbotContactList", response?.res?.data?.data || []);
    } else manageAdminState("chatbotContactList", []);
  };
  /**
   * Exports chatbot contacts as CSV.
   * @param {Object} date - Date range (unused in implementation).
   */
  const handleExportContactsCSV = async (date) => {
    manageAdminState("isCSVLoading", true);
    const response = await CallAPI(
      "CHAT",
      "GET",
      `${CHATBOT_CONTACT_LIST_EXPORT_CSV}`,
    );
    manageAdminState("isCSVLoading", false);
    // console.log("resPPPPPPP", response);
    if (response.res.status) {
      ExportCSV(response.res.data, "", false, false, true);
    }
  };

  /**
   * Exports chatbot contacts as CSV.
   * @param {Object} date - Date range (unused in implementation).
   */
  const handleExportVisitorContactsCSV = async (date) => {
    manageAdminState("isVisitorCSVLoading", true);
    const response = await CallAPI(
      "CHAT",
      "GET",
      `${CHATBOT_VISITOR_CONTACT_LIST_EXPORT_CSV}`,
    );
    manageAdminState("isVisitorCSVLoading", false);
    // console.log("resPPPPPPP", response);
    if (response.res.status) {
      ExportCSV(response.res.data, "", false, false, true);
    }
  };

  /**
   * Fetches all chatbot metadata.
   * @param {number} [page=1] - Page number.
   * @param {number} [limit=10] - Items per page.
   */
  const getAllChatbotMetaData = async (page = 1, limit = 10) => {
    fetchChatBotAllMeta(
      undefined,
      `${BOT_COMPLIANCE.get_All_list}?limit=${limit}&page=${page}&hasBot=true`,
    );
  };

  useEffect(() => {
    manageAdminState("isLoading", chatbotAllMetaLoading);
  }, [chatbotAllMetaLoading]);

  useEffect(() => {
    if (chatbotAllMetaRes?.res?.data?.status === 200) {
      manageAdminState(
        "chatbotOwnMetaData",
        chatbotAllMetaRes?.res?.data?.data || [],
      );
    }
  }, [chatbotAllMetaRes]);

  /**
   * Fetches own chatbot metadata.
   * @param {number} [page=1] - Page number.
   * @param {number} [limit=10] - Items per page.
   */
  const getAllOwnChatbotMetaData = async (page = 1, limit = 10) => {
    fetchChatBotOwnMeta(
      undefined,
      `${BOT_COMPLIANCE.get_own_list}?limit=${limit}&page=${page}&hasBot=true`,
    );
  };

  useEffect(() => {
    manageAdminState("isLoading", chatbotOwnMetaLoading);
  }, [chatbotOwnMetaLoading]);

  useEffect(() => {
    if (chatbotOwnMetaRes?.res?.data?.status) {
      manageAdminState(
        "chatbotOwnMetaData",
        chatbotOwnMetaRes?.res?.data?.data || [],
      );
    }
  }, [chatbotOwnMetaRes]);

  useEffect(() => {
    manageAdminState("isLoading", roleLoading);
  }, [roleLoading]);

  useEffect(() => {
    if (roleRes?.res?.data?.status) {
      manageAdminState("roleList", roleRes.res.data.data || []);
    }
  }, [roleRes]);

  useEffect(() => {
    manageAdminState("isLoading", allUserLoading);
  }, [allUserLoading]);

  useEffect(() => {
    // if (response?.res?.data?.status === 200) {
    //   console.log("res_user", response?.res?.data?.data);
    //   manageAdminState("usersList", response?.res?.data?.data || []);
    // } else manageAdminState("usersList", []);
    if (allUserRes?.res?.data?.status) {
      manageAdminState("usersList", allUserRes.res.data.data || []);
    }
  }, [allUserRes]);

  const coolBizAdminValues = {
    ...state,
    getAllUsers,
    deleteUsers,
    createCoupon,
    getAllCoupons,
    deleteCoupon,
    getAllPlan,
    getAllPlanDescription,
    createPlan,
    getPlanById,
    createCms,
    getAllCMS,
    deleteCms,
    updateCms,
    getCMSById,
    getAllContactUs,
    createUsers,
    EditUsers,
    exportCSV,
    getReveneuList,
    handleExportReveneuCSV,
    editPlan,
    getRoleList,
    createUpdateRole,
    getUserActivityOfAgency,
    getaffiliateCommisionList,
    handleExportCommissionCSV,
    getAdminAddedBaseModel,
    deleteCMSFile,
    downloadUserActivityOfAgency,
    getChatbotContactList,
    handleExportContactsCSV,
    handleExportVisitorContactsCSV,
    getAllOwnChatbotMetaData,
    getAllChatbotMetaData,
  };

  return (
    <AdminContext.Provider value={coolBizAdminValues}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
